/**
 * Onboard Club - Guided onboarding interface for platform admin to set up new clubs
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlatformAdminOnly } from '../../components/common/ProtectedComponent';
import apiService from '../../services/api';
import { AdminPageLayout, Card, CardHeader, CardTitle, CardContent, Section, Button, Badge, Grid, Col, FormInput, FormSelect, FormTextArea, PhoneNumberInput } from '../../components';
import { Building2, MapPin, User, Rocket, CheckCircle, PartyPopper } from 'lucide-react';
import './OnboardClub.css';

const OnboardClub = () => {
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Club Basics
    name: '',
    club_type: 'youth_soccer',
    description: '',

    // Step 2: Location & Contact
    contact_email: '',
    contact_phone: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',

    // Step 3: Admin User
    admin_first_name: '',
    admin_last_name: '',
    admin_email: '',
    admin_phone: '',
    admin_password: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [completedClub, setCompletedClub] = useState(null);

  const steps = [
    {
      number: 1,
      title: 'Club Basics',
      description: 'Essential information about the club',
      icon: <Building2 size={24} />
    },
    {
      number: 2,
      title: 'Location & Contact',
      description: 'Where to find the club',
      icon: <MapPin size={24} />
    },
    {
      number: 3,
      title: 'Admin User',
      description: 'Create the club administrator',
      icon: <User size={24} />
    },
    {
      number: 4,
      title: 'Review & Launch',
      description: 'Finalize and create the club',
      icon: <Rocket size={24} />
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        if (!formData.name.trim()) newErrors.name = 'Club name is required';
        if (!formData.club_type) newErrors.club_type = 'Club type is required';
        break;
      case 2:
        if (!formData.contact_email.trim()) newErrors.contact_email = 'Contact email is required';
        if (!formData.contact_phone.trim()) newErrors.contact_phone = 'Contact phone is required';
        if (!formData.city.trim()) newErrors.city = 'City is required';
        if (!formData.state.trim()) newErrors.state = 'State is required';

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.contact_email && !emailRegex.test(formData.contact_email)) {
          newErrors.contact_email = 'Please enter a valid email address';
        }
        break;
      case 3:
        if (!formData.admin_first_name.trim()) newErrors.admin_first_name = 'Admin first name is required';
        if (!formData.admin_last_name.trim()) newErrors.admin_last_name = 'Admin last name is required';
        if (!formData.admin_email.trim()) newErrors.admin_email = 'Admin email is required';
        if (!formData.admin_password.trim()) newErrors.admin_password = 'Admin password is required';

        const adminEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (formData.admin_email && !adminEmailRegex.test(formData.admin_email)) {
          newErrors.admin_email = 'Please enter a valid email address';
        }

        if (formData.admin_password && formData.admin_password.length < 6) {
          newErrors.admin_password = 'Password must be at least 6 characters';
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const result = await apiService.createClub(formData);
      setCompletedClub(result);
      setCurrentStep(5); // Success step
    } catch (err) {
      setErrors({
        submit: err.message || 'Failed to create club. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, admin_password: password }));
  };

  if (completedClub) {
    return (
      <PlatformAdminOnly>
        <AdminPageLayout
          title="ONBOARDING SUCCESS"
          subtitle="System initialization complete"
          backPath="/admin/clubs"
          maxWidth="1400px"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
            <Section title="Provisioning Successful">
              <Card level={1} variant="feature" style={{ padding: '4rem', textAlign: 'center' }}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  background: 'var(--color-success-light)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 2rem',
                  color: 'var(--color-success)'
                }}>
                  <PartyPopper size={50} />
                </div>
                <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>Welcome to the Ecosystem!</h2>
                <p style={{ opacity: 0.8, fontSize: '1.25rem', maxWidth: '700px', margin: '0 auto' }}>
                  The administrative architecture for <strong>{completedClub.club.name}</strong> has been fully initialized and is ready for operational deployment.
                </p>
              </Card>
            </Section>

            <Grid columns={3} gap="2rem">
              <Card level={1}>
                <CardHeader>
                  <CardTitle as="h3" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Building2 size={20} /> Club Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Club Name</label>
                      <div style={{ fontWeight: '600', marginTop: '0.25rem' }}>{completedClub.club.name}</div>
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Organization Type</label>
                      <div style={{ marginTop: '0.25rem' }}><Badge variant="outline">{completedClub.club.club_type?.toUpperCase()}</Badge></div>
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Regional Authority</label>
                      <div style={{ marginTop: '0.25rem' }}>{completedClub.club.city}, {completedClub.club.state}</div>
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Tenant ID</label>
                      <code style={{ fontSize: '0.85rem', color: 'var(--color-info)', display: 'block', marginTop: '0.25rem' }}>{completedClub.club.tenant_id}</code>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card level={1}>
                <CardHeader>
                  <CardTitle as="h3" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <User size={20} /> Primary Guardian
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Full Name</label>
                      <div style={{ fontWeight: '600', marginTop: '0.25rem' }}>{completedClub.admin_user.first_name} {completedClub.admin_user.last_name}</div>
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Email</label>
                      <div style={{ color: 'var(--color-primary)', marginTop: '0.25rem' }}>{completedClub.admin_user.email}</div>
                    </div>
                    <div>
                      <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Status</label>
                      <div style={{ marginTop: '0.25rem' }}><Badge variant="success">ACTIVE</Badge></div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card level={1}>
                <CardHeader>
                  <CardTitle as="h3" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <CheckCircle size={20} /> Protocol Checklist
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', opacity: 0.8 }}>
                      <CheckCircle size={16} style={{ color: 'var(--color-success)' }} /> Administrative Access Enabled
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', opacity: 0.8 }}>
                      <CheckCircle size={16} style={{ color: 'var(--color-success)' }} /> Program Scaffolding Ready
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', opacity: 0.8 }}>
                      <CheckCircle size={16} style={{ color: 'var(--color-success)' }} /> Coaching Registry Initialised
                    </li>
                    <li style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', opacity: 0.8 }}>
                      <CheckCircle size={16} style={{ color: 'var(--color-success)' }} /> Family Enrolment Phase Open
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </Grid>

            <Section>
              <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center' }}>
                <Button
                  onClick={() => navigate('/admin/clubs')}
                  variant="primary"
                  size="lg"
                >
                  Return to Management
                </Button>
                <Button
                  onClick={() => {
                    setCompletedClub(null);
                    setCurrentStep(1);
                    setFormData({
                      name: '',
                      club_type: 'youth_soccer',
                      description: '',
                      contact_email: '',
                      contact_phone: '',
                      website: '',
                      address: '',
                      city: '',
                      state: '',
                      zip_code: '',
                      admin_first_name: '',
                      admin_last_name: '',
                      admin_email: '',
                      admin_phone: '',
                      admin_password: ''
                    });
                  }}
                  variant="outline"
                  size="lg"
                >
                  Onboard Another Entity
                </Button>
              </div>
            </Section>
          </div>
        </AdminPageLayout>
      </PlatformAdminOnly>
    );
  }

  return (
    <PlatformAdminOnly>
      <AdminPageLayout
        title="ORGANIZATION ONBOARDING"
        subtitle="Strategic alignment wizard for new partners"
        backPath="/admin/clubs"
        maxWidth="1200px"
      >
        <Section>
          {/* Progress Steps */}
          <div className="progress-steps" style={{ marginBottom: '4rem' }}>
            {steps.map((step) => (
              <div
                key={step.number}
                className={`step ${currentStep >= step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}
              >
                <div className="step-icon">
                  {currentStep > step.number ? <CheckCircle size={24} /> : step.icon}
                </div>
                <div className="step-content">
                  <div className="step-title">{step.title}</div>
                  <div className="step-description">{step.description}</div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {errors.submit && (
          <Section>
            <div style={{ background: 'var(--color-error-light)', color: 'var(--color-error)', padding: '1.25rem', borderRadius: '12px', border: '1px solid var(--color-error)', textAlign: 'center', fontWeight: 600 }}>
              Deployment Blocked: {errors.submit}
            </div>
          </Section>
        )}

        {/* Step Content */}
        <div style={{ minHeight: '500px' }}>
          {currentStep === 1 && (
            <Section title="Step 1: Core Identity">
              <Card level={1}>
                <CardContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    <FormInput
                      label="Organization Legal Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      error={errors.name}
                      required
                      placeholder="e.g., Metropolitan Athletic Soccer Club"
                    />
                    <FormSelect
                      label="Governance Model"
                      name="club_type"
                      value={formData.club_type}
                      onChange={handleInputChange}
                      error={errors.club_type}
                      required
                      options={[
                        { value: 'youth_soccer', label: 'Youth Soccer Academy' },
                        { value: 'recreational', label: 'Community Recreational' },
                        { value: 'competitive', label: 'Elite Competitive' },
                        { value: 'academy', label: 'Professional Academy' }
                      ]}
                    />
                    <FormTextArea
                      label="Mission Statement & Description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={4}
                      placeholder="Outline the primary objectives and operational ethos..."
                    />
                  </div>
                </CardContent>
              </Card>
            </Section>
          )}

          {currentStep === 2 && (
            <Section title="Step 2: Operational Location">
              <Card level={1}>
                <CardContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    <Grid columns={2} gap="2rem">
                      <FormInput
                        label="Primary Contact Email"
                        type="email"
                        name="contact_email"
                        value={formData.contact_email}
                        onChange={handleInputChange}
                        error={errors.contact_email}
                        required
                        placeholder="operations@yourclub.com"
                      />
                      <PhoneNumberInput
                        label="Primary Contact Phone"
                        name="contact_phone"
                        value={formData.contact_phone}
                        onChange={handleInputChange}
                        error={errors.contact_phone}
                        required
                        placeholder="(555) 123-4567"
                      />
                    </Grid>
                    <FormInput
                      label="Official Digital Presence (Website)"
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://www.yourclub.com"
                    />
                    <FormInput
                      label="Physical Headquarters Address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="123 Soccer Field Lane"
                    />
                    <Grid columns={3} gap="2rem">
                      <FormInput
                        label="City"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        error={errors.city}
                        required
                        placeholder="Springfield"
                      />
                      <FormInput
                        label="State Territory"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        error={errors.state}
                        required
                        placeholder="CA"
                        maxLength={2}
                      />
                      <FormInput
                        label="Postcode"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleInputChange}
                        placeholder="12345"
                        maxLength={10}
                      />
                    </Grid>
                  </div>
                </CardContent>
              </Card>
            </Section>
          )}

          {currentStep === 3 && (
            <Section title="Step 3: Executive Guardianship">
              <Card level={1}>
                <CardContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    <Grid columns={2} gap="2rem">
                      <FormInput
                        label="First Name"
                        name="admin_first_name"
                        value={formData.admin_first_name}
                        onChange={handleInputChange}
                        error={errors.admin_first_name}
                        required
                        placeholder="Identity First Name"
                      />
                      <FormInput
                        label="Last Name"
                        name="admin_last_name"
                        value={formData.admin_last_name}
                        onChange={handleInputChange}
                        error={errors.admin_last_name}
                        required
                        placeholder="Identity Last Name"
                      />
                    </Grid>
                    <Grid columns={2} gap="2rem">
                      <FormInput
                        label="Security Email"
                        type="email"
                        name="admin_email"
                        value={formData.admin_email}
                        onChange={handleInputChange}
                        error={errors.admin_email}
                        required
                        placeholder="admin@yourclub.com"
                      />
                      <PhoneNumberInput
                        label="Contact phone (Optional)"
                        name="admin_phone"
                        value={formData.admin_phone}
                        onChange={handleInputChange}
                        placeholder="(555) 987-6543"
                      />
                    </Grid>

                    <div className="password-setup" style={{ backgroundColor: 'var(--surface-lvl2)', padding: '2rem', borderRadius: '16px', border: '1px dashed var(--color-border)' }}>
                      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                          <FormInput
                            label="Initial Security Protocol (Password)"
                            name="admin_password"
                            value={formData.admin_password}
                            onChange={handleInputChange}
                            error={errors.admin_password}
                            required
                            placeholder="Ensure high entropy"
                            help="Strategic access credential for initialization"
                          />
                        </div>
                        <Button
                          type="button"
                          onClick={generatePassword}
                          variant="outline"
                          size="md"
                          style={{ marginBottom: errors.admin_password ? '2rem' : '4px' }}
                        >
                          Auto-Generate
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Section>
          )}

          {currentStep === 4 && (
            <Section title="Step 4: Strategic Protocol Review">
              <Grid columns={12} gap="2rem">
                <Col span={8}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                    <Card level={1}>
                      <CardHeader>
                        <CardTitle as="h3">Core Identity Profile</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Grid columns={2} gap="2rem">
                          <div className="review-group">
                            <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Legal Name</label>
                            <div style={{ fontWeight: '500', marginTop: '0.25rem' }}>{formData.name}</div>
                          </div>
                          <div className="review-group">
                            <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Org Type</label>
                            <div style={{ marginTop: '0.25rem' }}><Badge variant="outline">{formData.club_type?.replace('_', ' ').toUpperCase()}</Badge></div>
                          </div>
                        </Grid>
                        {formData.description && (
                          <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border-light)' }}>
                            <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Mission</label>
                            <p style={{ marginTop: '0.5rem', fontSize: '0.95rem', opacity: 0.8 }}>{formData.description}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    <Card level={1}>
                      <CardHeader>
                        <CardTitle as="h3">Localization & Network</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Grid columns={2} gap="2rem">
                          <div className="review-group">
                            <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Regional Office</label>
                            <div style={{ marginTop: '0.25rem', opacity: 0.8 }}>
                              {formData.address && <>{formData.address}<br /></>}
                              {formData.city}, {formData.state} {formData.zip_code}
                            </div>
                          </div>
                          <div className="review-group">
                            <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Digital Gateway</label>
                            <div style={{ marginTop: '0.25rem', opacity: 0.8 }}>{formData.website || 'No official website linked'}</div>
                          </div>
                          <div className="review-group">
                            <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Primary Channel</label>
                            <div style={{ marginTop: '0.25rem', fontWeight: '500' }}>{formData.contact_email}</div>
                          </div>
                          <div className="review-group">
                            <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Verified Phone</label>
                            <div style={{ marginTop: '0.25rem', opacity: 0.8 }}>{formData.contact_phone}</div>
                          </div>
                        </Grid>
                      </CardContent>
                    </Card>
                  </div>
                </Col>

                <Col span={4}>
                  <Card level={1} height="100%" variant="feature">
                    <CardHeader>
                      <CardTitle as="h3">Assigned Authority</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        <div className="review-group">
                          <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Identity</label>
                          <div style={{ fontWeight: '600', marginTop: '0.25rem' }}>{formData.admin_first_name} {formData.admin_last_name}</div>
                        </div>
                        <div className="review-group">
                          <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Secure Email</label>
                          <div style={{ color: 'var(--color-primary)', marginTop: '0.25rem' }}>{formData.admin_email}</div>
                        </div>
                        <div className="review-group">
                          <label style={{ fontSize: '0.7rem', textTransform: 'uppercase', opacity: 0.5, fontWeight: '600' }}>Protocol Credential</label>
                          <div style={{ marginTop: '0.25rem' }}><Badge variant="outline">••••••••••••</Badge></div>
                        </div>
                        <div style={{ marginTop: 'auto', paddingTop: '2rem' }}>
                          <p style={{ fontSize: '0.85rem', opacity: 0.6, fontStyle: 'italic' }}>
                            By launching, you confirm this identity has been vetted for platform guardianship.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Col>
              </Grid>
            </Section>
          )}
        </div>

        {/* Navigation */}
        <Section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4rem', padding: '2rem 0', borderTop: '1px solid var(--color-border-light)' }}>
            <div>
              {currentStep > 1 && (
                <Button
                  onClick={handlePrevious}
                  variant="outline"
                  disabled={loading}
                  size="lg"
                >
                  Return to Phase {currentStep - 1}
                </Button>
              )}
            </div>

            <div>
              <span style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--color-text-tertiary)', background: 'var(--surface-lvl2)', padding: '0.5rem 1rem', borderRadius: '20px' }}>
                Operational Phase {currentStep} of 4
              </span>
            </div>

            <div>
              {currentStep < 4 ? (
                <Button
                  onClick={handleNext}
                  variant="primary"
                  size="lg"
                >
                  Proceed to Phase {currentStep + 1}
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  variant="save"
                  disabled={loading}
                  loading={loading}
                  size="lg"
                  style={{ minWidth: '200px' }}
                >
                  Finalise Deployment
                </Button>
              )}
            </div>
          </div>
        </Section>
      </AdminPageLayout>
    </PlatformAdminOnly>
  );
};

export default OnboardClub;